import { Grid, useEventCallback } from '@mui/material';
import { memo, useCallback, useMemo, useState } from 'react';
import { transformerPersonToRegisterOffice } from 'shell/components/shared/sipo-person-form/transformer-person-to-register-office';
import { __PERSON_NEW_API_URL__ } from 'shell/constants/environment';
import { useHttpClient } from 'shell/hooks/use-http-fetch/stateless';
import { ResponsePromise } from 'shell/hooks/use-http-fetch/stateless/type';
import { HttpResponse } from 'shell/models/http-response';
import { ResponseAnprData } from 'shell/models/person';
import { noop } from 'shell/utils/noop';
import Table from 'shell/components/shared/table';
import SearchMessages, { MessageType } from 'shell/components/shared/search-messages';
import { ItemVM } from 'shell/view-models/item';
import AccordionFilter from './accordion-filter/index';
import {
  BodyTablePersonsVM,
  PersonalDataSearchVM,
  PersonalDataVM,
} from 'shell/view-models/personal-data';
import { HeadCell } from '../table/types';

export const headCells: HeadCell<BodyTablePersonsVM, keyof BodyTablePersonsVM>[] = [
  {
    id: 'fiscalCode',
    label: 'Codice Fiscale',
  },
  {
    id: 'surname',
    label: 'Cognome',
  },
  {
    id: 'name',
    label: 'Nome',
  },
  {
    id: 'gender',
    label: 'Sesso',
  },
  {
    id: 'birthday',
    label: 'Data di nascita',
  },
  {
    id: 'birthplace',
    label: 'Luogo di nascita',
  },
  {
    id: 'residence',
    label: 'Residenza',
  },
  {
    id: 'state',
    label: 'Stato',
  },
  {
    id: 'anpr',
    label: 'ANPR',
  },
  {
    id: 'action',
    label: '',
  },
];

interface FormSearchPersonProps {
  onChangeFields: (values: PersonalDataSearchVM) => void;
  onChange: (
    key: keyof PersonalDataSearchVM,
    value: PersonalDataSearchVM[keyof PersonalDataSearchVM]
  ) => void;
  values?: PersonalDataSearchVM;
  disabled?: boolean;
  resetForm?: () => void;
  url?: string;
  typeRegister?: string;
  yearId?: string;
  progressive?: string;
}

const FormSearchPersonComponent = ({
  values,
  onChange,
  resetForm,
  disabled,
  onChangeFields,
  url = `${__PERSON_NEW_API_URL__}/persona`,
}: FormSearchPersonProps): React.ReactElement => {
  // ricerca sia su ANPR che DB
  const getPerson = useHttpClient<HttpResponse<ResponseAnprData>, PersonalDataSearchVM>(url, {
    options: {
      errorCatching: true,
      method: 'POST',
      loading: true,
      data: {},
    },
    transformer: (response) => {
      const data = transformerPersonToRegisterOffice(response, true);

      return {
        ...data.applicantData,
        warnings: (response.data.payload?.risposta3002OK?.listaAnomalie?.errore || []).map(
          (warning) => ({
            id: warning.codiceErroreAnomalia,
            value: warning.testoErroreAnomalia,
          })
        ),
        errors: (response.data.payload?.rispostaKO?.listaErrori?.errore || []).map((error) => ({
          id: error.codiceErroreAnomalia,
          value: error.testoErroreAnomalia,
        })),
      };
    },
  });

  const onSearch = useEventCallback(() => {
    setVisibility({});

    const hasSearchBirthFilters = !!values?.birthdayChoice && !!values?.birthday;

    const data = {
      datiRichiesta: {
        ricercaNazionale: '1', // fixed
      },
      datiNascita: hasSearchBirthFilters
        ? {
            senzaGiorno:
              !values?.birthdayChoice?.includes('day') && values?.birthdayChoice?.includes('month')
                ? '1'
                : undefined,
            senzaGiornoMese:
              !values?.birthdayChoice?.includes('day') && !values?.birthdayChoice?.includes('month')
                ? '1'
                : undefined,
            dataEvento: values?.birthday?.toFormat('yyyy-MM-dd') || undefined,
          }
        : undefined,
      criteriRicerca: {
        codiceFiscale: values?.fiscalCode || undefined,
        cognome: values?.surname || undefined,
        nome: values?.name || undefined,
        senzaCognome: values?.withoutSurname ? '1' : undefined,
        senzaNome: values?.withoutName ? '1' : undefined,
      },
    };

    (
      getPerson({ data }) as ResponsePromise<HttpResponse<ResponseAnprData>, PersonalDataSearchVM>
    ).then((response) => {
      const castedResponse = response as PersonalDataSearchVM;

      onChangeFields({
        ...castedResponse,
      });
    });
  });

  const onChangePage = useEventCallback((page: number) => {
    onChange('table', {
      ...(values?.table || {}),
      page: page || 0,
    });
  });

  const onChangeRowsPerPage = useEventCallback((rowsPerPage: number) => {
    onChange('table', {
      ...(values?.table || {}),
      page: 0,
      rowsPerPage: rowsPerPage || 0,
    });
  });

  const onVisibilityPress = useEventCallback(
    (cell: BodyTablePersonsVM): Promise<BodyTablePersonsVM> => {
      return new Promise(() => {
        (
          getPerson({
            data: {
              criteriRicerca: {
                codiceFiscale: cell.fiscalCode || undefined,
                idSchedaSoggettoANPR: cell.anpr || undefined,
              },
              datiRichiesta: {
                ricercaNazionale: '1',
              },
            },
          }) as ResponsePromise<HttpResponse<ResponseAnprData>, PersonalDataSearchVM>
        ).then((response) => {
          const castedResponse = response as PersonalDataSearchVM;

          //call the call back function which sets the subjectData

          onChangeFields({
            ...(castedResponse || {}),
            table: undefined, // reset table
            withSearch: true, // la persona è il risultato della ricerca
          });
        });
      });
    }
  );

  const [visibility, setVisibility] = useState<{ error?: boolean; warning?: boolean }>({
    error: undefined,
    warning: undefined,
  });

  const onVisibility = useCallback(
    (open, type) => {
      const visibilityMessage = type === 'error' && !open ? false : open;

      setVisibility((oldState) => ({ ...oldState, [type]: visibilityMessage }));
    },
    [setVisibility]
  );

  const errorMessage = useMemo(() => {
    const errorToVisualize: { visibility?: boolean; title?: string; messages?: Array<ItemVM> } = {
      visibility: false,
      title: '',
    };

    if (values?.errors && values?.errors?.length > 0 && visibility.error === undefined) {
      errorToVisualize.title =
        values?.errors?.length > 1 ? `Ci sono ${values?.errors?.length} errori` : `C'è 1 errore`;
      errorToVisualize.messages = values?.errors;
      errorToVisualize.visibility = true;
      return errorToVisualize;
    }

    errorToVisualize.visibility = visibility.error;

    return errorToVisualize;
  }, [visibility.error, values?.errors]);

  return (
    <Grid container item xs={12} spacing={2}>
      {!errorMessage.title || !values?.warnings ? undefined : (
        <Grid item xs={12}>
          <SearchMessages
            type={MessageType.Error}
            title={errorMessage.title || ''}
            messages={errorMessage.messages || []}
            visibility={errorMessage.visibility || false}
            onChangeVisibility={onVisibility}
          />
          <SearchMessages
            type={MessageType.Warning}
            title={`Ci sono ${values?.warnings?.length || ''} warning`}
            messages={values?.warnings || []}
            visibility={
              visibility.warning !== undefined
                ? visibility.warning
                : (values?.warnings || [])?.length > 0
            }
            onChangeVisibility={onVisibility}
          />
        </Grid>
      )}
      <Grid item xs={12}>
        <AccordionFilter
          disabled={disabled}
          onChangeFields={onChangeFields}
          resetForm={resetForm}
          onChange={
            onChange as (
              key: keyof PersonalDataVM,
              value: PersonalDataVM[keyof PersonalDataVM]
            ) => void
          }
          values={values}
          onSearch={onSearch}
        />
      </Grid>
      {values?.table?.body ? (
        <Grid item xs={12}>
          <Table
            ariaLabel="Lista Persone"
            page={values?.table?.page || 0}
            rowsPerPage={values?.table?.rowsPerPage}
            body={(values?.table?.body || []) as Array<BodyTablePersonsVM>}
            count={values?.table?.body?.length || 0}
            head={headCells}
            onSort={noop}
            onPageChange={onChangePage}
            onRowsPerPageChange={onChangeRowsPerPage}
            orders={{}}
            isNotLayoutFixed
            noSort
            onVisibilityPress={onVisibilityPress}
            //hideButtons
          />
        </Grid>
      ) : undefined}
    </Grid>
  );
};

FormSearchPersonComponent.displayName = 'Form search person component for new person page';

const FormSearchPersonNewPerson = memo(FormSearchPersonComponent);

export default FormSearchPersonNewPerson;
